import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { MessageContext } from "../../../../common/message";
import UploadButton from "../../../../common/upload/UploadButton";
import { allowedFileSize } from "../requestSubmissionValidator";
import { UPLOAD_STATUS } from "./DocumentUpload";
import { getAccessToken } from "../../../../common/authorization/AuthorizeService";

export const uploadPercentage = (loaded, total) => {
  if (!loaded || !total || total === 0) {
    return 0;
  }
  return (loaded / total) * 100;
};

export const onloadstart = (doc, file, dispatchUpdateAction) => {
  doc.href = null;
  doc.uploadError = null;
  doc.uploadProgress = 0;
  const { name, size, type, lastModified } = file;
  doc.file = { name, size, type, lastModified };
  doc.uploadStatus = UPLOAD_STATUS.IN_PROGRESS;
  dispatchUpdateAction();
};

export const onprogress = (doc, loaded, total, dispatchUpdateAction) => {
  doc.uploadProgress = uploadPercentage(loaded, total);
  dispatchUpdateAction();
};

export const onerror = (doc, error, dispatchUpdateAction) => {
  doc.uploadStatus = UPLOAD_STATUS.ERROR;
  doc.uploadError = error;
  doc.uploadProgress = 0;
  dispatchUpdateAction();
};

export const onload = (doc, dispatchUpdateAction) => {
  doc.uploadStatus = UPLOAD_STATUS.SUCCESS;
  dispatchUpdateAction();
};

export const onloadend = (doc, message, t) => {
  if (doc.uploadStatus === UPLOAD_STATUS.ERROR) {
    message.toggle({
      title: t("modalMessage.titleInfo"),
      content: t("requests.submission.upload.uploadError"),
      hideNo: true,
      yesLabel: t("modalMessage.okLabel"),
      onYes: () => message.toggle({}),
    });
  }
};

export const checkForHttpErrors = (request) =>
  function () {
    if (![200, 201].includes(request.status)) {
      request.upload.onerror();
      request.upload.onloadend();
    }
  };

function DocumentUploadButton({
  doc,
  uploadUrl = `${process.env.REACT_APP_GATEWAY_URL}/upload`,
  maxDocumentSizeInMb,
  acceptDocumentTypes = "",
  dispatchUpdateAction,
  handleResponse = (e) => {
    if (e.responseText) {
      try {
        let { href } = JSON.parse(e.responseText);
        doc.href = href;
        dispatchUpdateAction();
      } catch (e) {
        // Handle error
      }
    }
  },
  formDataAsJson = {},
}) {
  const message = useContext(MessageContext);
  const { t } = useTranslation();

  const upload = async (e) => {
    const file = e.currentTarget.files[0];

    if (allowedFileSize(file.size, maxDocumentSizeInMb)) {
      const formData = new FormData();
      formData.append("file", file);
      Object.keys(formDataAsJson).forEach((key) => formData.append(key, formDataAsJson[key]));

      const request = new XMLHttpRequest();

      request.upload.onloadstart = () => onloadstart(doc, file, dispatchUpdateAction);

      request.upload.onprogress = (e1) =>
        onprogress(doc, e1.loaded, e1.total, dispatchUpdateAction);

      request.upload.ontimeout = (e) => onerror(doc, e, dispatchUpdateAction);

      request.upload.onerror = (e) => onerror(doc, e, dispatchUpdateAction);

      request.upload.onload = (e) => onload(doc, dispatchUpdateAction);

      request.upload.onloadend = (e) => onloadend(doc, message, t);

      request.onreadystatechange = function () {
        if (this.readyState === 4) {
          handleResponse(this);
        }
      };
      // Check for request errors such as 404 etc. on the main XMLHttpRequest after upload has finished
      // XMLHttpRequest.upload does not report http status
      request.onload = checkForHttpErrors(request);

      request.open("POST", uploadUrl);
      let token = await getAccessToken();

      request.setRequestHeader("Request-Origin", "portal");
      request.setRequestHeader("Authorization", `Bearer ${token}`);
      request.send(formData);
    } else {
      message.toggle({
        title: t("modalMessage.titleInfo"),
        content: t("requests.submission.upload.fileSizeLimit", {
          maxDocumentSizeInMb: maxDocumentSizeInMb,
        }),
        hideNo: true,
        yesLabel: t("modalMessage.OKLabel"),
        onYes: () => message.toggle({}),
      });
    }
  };

  return (
    <UploadButton
      acceptDocumentTypes={acceptDocumentTypes}
      className={
        doc.uploadStatus !== UPLOAD_STATUS.SUCCESS &&
        doc.uploadStatus !== UPLOAD_STATUS.IN_PROGRESS &&
        doc.label &&
        doc.label.length > 0
          ? ""
          : "is-invisible"
      }
      onChange={upload}
      label={t("requests.submission.upload.chooseFile")}
    />
  );
}

export default DocumentUploadButton;
